import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { NxMark } from '../components/icons.js';

/**
 * Tela de redefinição de senha.
 *
 * Chega aqui pelo link enviado no e-mail: `/reset-password?token=<token>`.
 * Sem o token na URL, mostra estado de erro amigável apontando pro fluxo
 * de "esqueci minha senha".
 *
 * Regra: se o backend responder `mustLoginWith2fa: true`, redirecionamos
 * pro /login com um aviso — senha foi trocada mas ainda falta o 2FA.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const nav = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const rules = useMemo(
    () => [
      { label: 'mínimo 8 caracteres', ok: password.length >= 8 },
      { label: 'pelo menos uma letra', ok: /[A-Za-z]/.test(password) },
      { label: 'pelo menos um número', ok: /\d/.test(password) },
      { label: 'confirmação idêntica', ok: password.length > 0 && password === confirm },
    ],
    [password, confirm],
  );
  const allOk = rules.every((r) => r.ok);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!allOk || !token) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.passwordRecovery.reset(token, password);
      setDone(true);
      // Curta pausa pro usuário ver a mensagem antes de redirecionar.
      setTimeout(() => {
        if (res.mustLoginWith2fa) {
          nav('/login?resetOk=1&twofa=1', { replace: true });
        } else {
          nav('/login?resetOk=1', { replace: true });
        }
      }, 900);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 503) {
          setError('A recuperação de senha não está disponível no momento.');
        } else if (err.status === 400) {
          if (err.code === 'invalid_or_expired_token') {
            setError(
              'Este link é inválido ou já expirou. Peça um novo em "Esqueci minha senha".',
            );
          } else {
            setError('A nova senha não atende aos requisitos.');
          }
        } else if (err.status === 429) {
          setError('Muitas tentativas. Aguarde alguns minutos.');
        } else {
          setError('Não foi possível redefinir a senha. Tente novamente.');
        }
      } else {
        setError('Erro de rede.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const year = new Date().getFullYear();

  return (
    <div className="nx-login">
      <div className="nx-login__stage">
        <div className="nx-login__hero">
          <span className="nx-login__hero-eyebrow">Acesso</span>
          <h2 className="nx-login__hero-title">
            Escolha uma <em>nova senha</em>.
          </h2>
          <p className="nx-login__hero-lead">
            Depois de salvar, o link deste e-mail deixa de funcionar. Se você
            usa 2FA, também precisará do código ao entrar de novo.
          </p>
        </div>

        <div className="nx-login__card">
          <div className="nx-login__card-head">
            <div className="nx-wordmark nx-login__brand" aria-label="Nxvolt">
              <span className="nx-wordmark__mark">
                <NxMark size={30} />
              </span>
              <span className="nx-wordmark__text">
                <b>NX</b>
                <i>volt</i>
              </span>
            </div>
            <div className="nx-login__parent">uma solução Noxxon Sat</div>
          </div>

          <h1 className="nx-login__title">Redefinir senha</h1>

          {!token ? (
            <>
              <div role="alert" className="nx-error" style={{ marginTop: 'var(--nx-space-4)' }}>
                Link inválido ou incompleto. Peça um novo em "Esqueci minha senha".
              </div>
              <div style={{ marginTop: 'var(--nx-space-6)' }}>
                <Link to="/forgot-password" className="nx-btn nx-btn--full">
                  Ir para "Esqueci minha senha"
                </Link>
              </div>
            </>
          ) : done ? (
            <div className="nx-success" role="status" style={{ marginTop: 'var(--nx-space-4)' }}>
              ✓ Senha redefinida. Redirecionando para o login…
            </div>
          ) : (
            <>
              <p className="nx-login__hint">
                A senha nova precisa ter pelo menos 8 caracteres, com letra e
                número.
              </p>
              <form onSubmit={onSubmit} noValidate>
                <label className="nx-field">
                  <span>Nova senha</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    autoFocus
                  />
                </label>
                <label className="nx-field">
                  <span>Confirmar nova senha</span>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </label>

                <ul className="nx-rules" aria-live="polite">
                  {rules.map((r) => (
                    <li key={r.label} className={r.ok ? 'nx-rules__ok' : ''}>
                      {r.ok ? '✓' : '○'} {r.label}
                    </li>
                  ))}
                </ul>

                {error && (
                  <div role="alert" className="nx-error">
                    {error}
                  </div>
                )}

                <div style={{ marginTop: 'var(--nx-space-6)' }}>
                  <button
                    className="nx-btn nx-btn--full"
                    type="submit"
                    disabled={submitting || !allOk}
                  >
                    {submitting ? 'Salvando…' : 'Salvar nova senha'}
                  </button>
                </div>
              </form>
              <div
                style={{
                  marginTop: 'var(--nx-space-4)',
                  textAlign: 'center',
                }}
              >
                <Link to="/login" className="nx-btn nx-btn--link">
                  ← Voltar ao login
                </Link>
              </div>
            </>
          )}

          <div className="nx-login__footer">© {year} Noxxon Sat · Nxvolt</div>
        </div>
      </div>
    </div>
  );
}
